//@storybook-skip: this is a container
import type { BaseItemSchema } from "@memorang/types";
import Box from "@memorang/ui/components/Box";
import FillInTheBlanksDragAndDrop from "@memorang/ui/components/StudySession/SessionContent/item-types/FillInTheBlanksDragAndDrop";
import { useCallback, useMemo, useState } from "react";
import {
	isBlankValuesResponse,
	toPersistableBlankValues,
} from "../../../helpers/ai-feedback";
import { normalizeBlankStem } from "../../../helpers/blank-tokens";
import useAIFeedbackStore from "../../../hooks/useAIFeedbackStore";
import useItemAnsweredState from "../../../hooks/useItemAnsweredState";
import DesktopFeedbackPanel from "./DesktopFeedbackPanel";

type Props = {
	item: BaseItemSchema;
	isSplitLayout: boolean;
};

const FibDragDropContainer = ({ item, isSplitLayout }: Props) => {
	const { content, response, scoring } = item;
	const itemId = item.id!;

	const { isAnswered, inReviewMode } = useItemAnsweredState(item);

	const setUserResponse = useAIFeedbackStore((s) => s.setUserResponse);

	const rawStem =
		content.stimulus?.type === "TEXT" ? (content.stimulus.text ?? "") : "";
	const stem = useMemo(() => normalizeBlankStem(rawStem), [rawStem]);

	const [values, setValues] = useState<Record<string, string>>(() => {
		const stored = useAIFeedbackStore.getState().userResponse?.[itemId];
		if (isBlankValuesResponse(stored)) {
			return stored;
		}
		return item.answer?.selectedBlankValues ?? {};
	});

	const handleChangeValues = useCallback(
		(nextValues: Record<string, string>) => {
			setValues(nextValues);
			setUserResponse(itemId, toPersistableBlankValues(nextValues));
		},
		[itemId, setUserResponse],
	);

	const wordBank = response.type === "TOKEN_MAPPING" ? response.wordBank : [];
	const instruction =
		response.type === "TOKEN_MAPPING" ? (response.instruction ?? "") : "";

	const correctWordIdsByBlank = useMemo(():
		| Record<string, string>
		| undefined => {
		if (
			!(scoring && "type" in scoring) ||
			scoring.type !== "ANSWER_KEY_MAPPING"
		) {
			return undefined;
		}
		const map: Record<string, string> = {};
		for (const entry of scoring.answerKey) {
			if ("blankId" in entry) {
				map[entry.blankId] = "wordId" in entry ? entry.wordId : entry.choiceId;
			}
		}
		return map;
	}, [scoring]);

	return (
		<Box gap={16}>
			<FillInTheBlanksDragAndDrop
				instruction={instruction}
				stem={stem}
				wordBank={wordBank}
				values={values}
				onChangeValues={handleChangeValues}
				disabled={isAnswered}
				isAnswered={isAnswered}
				inReviewMode={inReviewMode}
				correctWordIdsByBlank={isAnswered ? correctWordIdsByBlank : undefined}
			/>
			<DesktopFeedbackPanel isSplitLayout={isSplitLayout} />
		</Box>
	);
};

export default FibDragDropContainer;
